import { useMemo } from 'react';

import { VENDORED_DEPENDENCY_DETECTION } from '../../../../../shared/featureFlags/flagNames';
import { DependencySource } from '../../../../../shared/vendoredDependencies';
import { useParams } from '../../../../contexts/RouterContext';
import { useIsFeatureEnabled } from '../../../../helpers/features';
import useGetReleaseGroupComponentCount from '../../../../hooks/api/binary/useGetReleaseGroupComponentCount';
import useGetRevisionComponentCount from '../../../../hooks/api/binary/useGetRevisionComponentCount';
import { useDependenciesCount } from '../../../../hooks/api/dependencies/useDependenciesCount';
import { useReleaseGroupDependenciesCount } from '../../../../hooks/api/releaseGroups/useReleaseGroupDependenciesCount';
import { useSnippetsCount } from '../../../../hooks/api/snippets/useSnippetsCount';
import { checkProjectScope, checkReleaseGroupScope } from '../types';
import type { RevisionRouteParams } from '../../projectDashboardTypes';
import type { InventoryScope, UseInventoryDependencyCountsResult, UseInventoryDropdownFeaturesResult } from '../types';

const ROOT_PATH = '/';

const MANAGED_COUNT_PARAMS = { sources: [DependencySource.Managed] };
const VENDORED_COUNT_PARAMS = { sources: [DependencySource.Vendored] };

type UseInventoryDependencyCountsParams = {
  scope: InventoryScope;
  features: UseInventoryDropdownFeaturesResult;
  shouldFetch?: boolean;
  fallbackProjectRevisionLocator?: string | null;
};

/**
 *
 * Note: Some of this looks a bit silly due to the fact that you can't conditionally call hooks based on the scope type.
 * @param {UseInventoryDependencyCountsParams} params - Params for inventory count fetching.
 */
export function useInventoryDependencyCounts({
  scope,
  features,
  shouldFetch = true,
  fallbackProjectRevisionLocator = null,
}: UseInventoryDependencyCountsParams): UseInventoryDependencyCountsResult {
  //  We use the type guards throughout to ensure type safety.
  const isProjectScope = checkProjectScope(scope);
  const isReleaseGroupScope = checkReleaseGroupScope(scope);

  //  The revision part isn't included in the scope, so we need to include it here
  const { revision_id: revisionId } = useParams<RevisionRouteParams>();
  const revisionLocatorFromURL = isProjectScope && revisionId ? `${scope.revision}$${revisionId}` : '';
  const userRevisionLocator = isProjectScope ? revisionLocatorFromURL || fallbackProjectRevisionLocator || '' : '';
  const shouldFetchProjectScopeCounts = isProjectScope && shouldFetch && !!userRevisionLocator;

  const isVendoredDependenciesEnabled = useIsFeatureEnabled(VENDORED_DEPENDENCY_DETECTION);

  /**
   * Managed/regular dependencies - Projects
   */
  const managedDependenciesCount = useDependenciesCount(
    userRevisionLocator,
    MANAGED_COUNT_PARAMS,
    shouldFetchProjectScopeCounts,
  );
  /**
   * Managed/regular dependencies - Release Groups
   */
  const releaseGroupManagedDependenciesCount = useReleaseGroupDependenciesCount(
    isReleaseGroupScope ? scope.projectGroupId : 0,
    isReleaseGroupScope ? scope.projectGroupReleaseId : 0,
    MANAGED_COUNT_PARAMS,
    isReleaseGroupScope && shouldFetch,
  );
  /**
   * Snippets - Projects (only)
   */
  const snippetsCount = useSnippetsCount(
    userRevisionLocator,
    { path: ROOT_PATH },
    shouldFetchProjectScopeCounts && features.shouldShowSnippets,
  );
  /**
   * Vendored dependencies - Projects
   */
  const vendoredDependenciesCount = useDependenciesCount(
    userRevisionLocator,
    VENDORED_COUNT_PARAMS,
    shouldFetchProjectScopeCounts && features.shouldShowVendored && isVendoredDependenciesEnabled,
  );
  /**
   * Vendored dependencies - Release Groups
   */
  const releaseGroupVendoredDependenciesCount = useReleaseGroupDependenciesCount(
    isReleaseGroupScope ? scope.projectGroupId : 0,
    isReleaseGroupScope ? scope.projectGroupReleaseId : 0,
    VENDORED_COUNT_PARAMS,
    isReleaseGroupScope && features.shouldShowVendored && isVendoredDependenciesEnabled && shouldFetch,
  );
  /**
   * Binary Analysis - Projects
   */
  const binaryAnalysis = useGetRevisionComponentCount(
    userRevisionLocator,
    shouldFetchProjectScopeCounts && features.shouldShowBinaryAnalysis,
  );
  /**
   * Binary Analysis - Release Groups
   */
  const releaseGroupBinaryAnalysis = useGetReleaseGroupComponentCount(
    isReleaseGroupScope ? scope.projectGroupId : 0,
    isReleaseGroupScope ? scope.projectGroupReleaseId : 0,
    isReleaseGroupScope && features.shouldShowBinaryAnalysis && shouldFetch,
  );

  //  React Query quirk: When a query has `enabled: false`, the `isLoading` state can be unreliable
  //  and may remain `true` even though the query is disabled. We only check loading state when the
  //  query is actually enabled to avoid false positives.
  const isManagedLoading = isProjectScope
    ? managedDependenciesCount.isLoading
    : releaseGroupManagedDependenciesCount.isLoading;
  const isSnippetsLoading = features.shouldShowSnippets && snippetsCount.isLoading;
  const isVendoredLoading = features.shouldShowVendored && (isProjectScope
    ? vendoredDependenciesCount.isLoading
    : releaseGroupVendoredDependenciesCount.isLoading);
  const isBinaryAnalysisLoading = features.shouldShowBinaryAnalysis && (isProjectScope
    ? binaryAnalysis.isLoading
    : releaseGroupBinaryAnalysis.isLoading);

  const isAnyLoading = isManagedLoading || isSnippetsLoading || isVendoredLoading || isBinaryAnalysisLoading;

  const managedCount = isProjectScope
    ? managedDependenciesCount.data?.count
    : releaseGroupManagedDependenciesCount.data?.count;
  const vendoredCount = isProjectScope
    ? vendoredDependenciesCount.data?.count
    : releaseGroupVendoredDependenciesCount.data?.count;
  const snippetsTotalCount = snippetsCount.data?.count;
  const binaryAnalysisCount = isProjectScope
    ? binaryAnalysis.data?.count
    : releaseGroupBinaryAnalysis.data?.count;

  const totalCount = (
    (managedCount ?? 0) +
    (vendoredCount ?? 0) +
    (snippetsTotalCount ?? 0) +
    (binaryAnalysisCount ?? 0)
  );

  return useMemo<UseInventoryDependencyCountsResult>(() => {
    return {
      isLoading: {
        any: isAnyLoading,
        managed: isManagedLoading,
        snippets: isSnippetsLoading,
        vendored: isVendoredLoading,
        binary: isBinaryAnalysisLoading,
      },
      counts: {
        total: totalCount,
        managed: managedCount ?? null,
        vendored: vendoredCount ?? null,
        snippets: snippetsTotalCount ?? null,
        binary: binaryAnalysisCount ?? null,
      },
    };
  }, [
    isAnyLoading,
    isManagedLoading,
    isSnippetsLoading,
    isVendoredLoading,
    isBinaryAnalysisLoading,
    totalCount,
    managedCount,
    vendoredCount,
    snippetsTotalCount,
    binaryAnalysisCount,
  ]);
}
